import { parseUsageLimitResponse, usageLimitMessageKey, type UsageLimitResponse } from '#shared/usage/usageLimit'
import type { UsageCategory } from '#shared/usage/surfaces'
import { useActivityStore } from '~/stores/activity'
import { useLazyT } from '~/utils/lazyI18n'

/**
 * The organization ran out of allowance — the red banner, and its Activity item (MAR-1009).
 *
 * `services/llm-gateway` is the only thing that decides quota; when it refuses a model call the block
 * travels to the browser TYPED (`shared/usage/usageLimit.ts`) along whichever path the call took:
 *   * a `429 USAGE_LIMIT_REACHED` on any API call — `plugins/api.ts` and `useAuthFetch` hand every
 *     error response here;
 *   * the chat stream's `usage_limit` event (the stream is already open, so it cannot be a status);
 *   * a sheet or workflow run stream's `run_failed`, whose error carries `usageLimit`;
 *   * the indexing tray, for a document whose read was refused (`quota_blocked` with its plan).
 * Every one of them ends in `report`, so the banner and the Activity item cannot disagree.
 *
 * The Activity item is session memory (no table, no dismiss endpoint): the block itself is the
 * gateway's live state, and the next refused call records it again. One item per category, so a
 * retried call or two streams reporting the same refusal stay one row.
 */
export interface UsageLimitInfo {
  planLabel: string
  category: UsageCategory | null
  reason: 'exhausted' | 'exceeds_plan'
}

export function useUsageLimit() {
  const blocked = useState('usage-limit-blocked', () => false)
  const info = useState<UsageLimitInfo | null>('usage-limit-info', () => null)

  function report(block: UsageLimitResponse) {
    info.value = { planLabel: planLabelOf(block), category: block.category, reason: block.reason }
    blocked.value = true
    recordUsageLimitActivity(block)
  }

  function dismiss() {
    blocked.value = false
  }

  function handleResponseError(statusCode: number | undefined, data: any) {
    if (statusCode !== 429) return
    const block = parseUsageLimitResponse(data)
    if (block) report(block)
  }

  return { blocked: readonly(blocked) as Readonly<Ref<boolean>>, info: readonly(info), dismiss, handleResponseError, report }
}

/**
 * `report`, for a caller that is not a setup function — a stream callback, a store action, a poll
 * tick. Resolved at the moment of the report rather than captured up front, so the composables that
 * read a stream need no new dependency of their own (and their tests no new stub). On the client
 * `useState` resolves through the app-wide Nuxt instance, so this works outside a component.
 */
export function reportUsageLimit(block: UsageLimitResponse) {
  try {
    useUsageLimit().report(block)
  } catch {
    // No Nuxt app in reach (a unit test, an SSR edge): there is no banner to raise there either.
  }
}

function planLabelOf(block: UsageLimitResponse): string {
  return block.planLabel ?? block.plan ?? 'Light'
}

/**
 * The `usage` Activity item alone, without raising the banner — for a producer that learns of a
 * block the banner was already raised for (the activity feed reading back a refused sheet run whose
 * stream reported it). `report` calls it too, so the two can never describe the block differently.
 */
export function recordUsageLimitActivity(block: UsageLimitResponse) {
  const planLabel = planLabelOf(block)
  try {
    const t = useLazyT()
    useActivityStore().upsert({
      id: `usage-limit:${block.category ?? 'all'}`,
      kind: 'usage',
      title: t('activity.usageLimit.title'),
      detail: t(usageLimitMessageKey(block), { plan: planLabel }),
      state: 'attention',
      // Amber, not red: nothing broke (`shared/activity/types.ts`'s `ActivityTone`).
      badge: { text: t('activity.usageLimit.badge'), tone: 'warn' },
      actions: [{ id: 'usage', label: t('service.viewUsage'), kind: 'link', to: '/app/settings/organization' }],
      startedAt: Date.now(),
    })
  } catch {
    // No Pinia (an SSR edge, a page outside the app shell): the banner still shows; the row is a
    // second surface for the same fact, never a reason to fail the caller's request handling.
  }
}
